<template>
  <div class="overflow-x-auto min-h-[200px]">
    <table class="min-w-full divide-y divide-gray-200">
      <thead class="bg-gray-50">
        <tr>
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            City
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            {{ periodComparisonLabel }}
          </th>
        </tr>
      </thead>
      <tbody
        class="bg-white divide-y divide-gray-200"
      >
        <tr
          v-for="city in tableData"
          :key="city.city"
          class="hover:bg-gray-50"
        >
          <td class="px-6 py-4 whitespace-nowrap">
            {{ city.city }}
          </td>
          <td class="px-6 py-4 whitespace-nowrap">
            <div class="flex items-center">
              <span
                :class="{
                  'text-green-700': city.is_above,
                  'text-red-700': !city.is_above
                }"
                class="font-medium"
              >
                {{ city.difference_text }}
              </span>
              <Icon
                :name="
                  city.is_above
                    ? 'i-heroicons-arrow-up'
                    : 'i-heroicons-arrow-down'
                "
                class="w-4 h-4 ml-1"
                :class="{
                  'text-green-700': city.is_above,
                  'text-red-700': !city.is_above
                }"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <div
      v-if="tableData.length === 0 && !isLoading"
      class="text-center py-8 text-gray-500 min-h-[150px] flex items-center justify-center"
    >
      No city data available for this role in {{ state }}
    </div>
    <div
      v-if="isLoading"
      class="text-center py-8 min-h-[150px] flex items-center justify-center"
    >
      <Icon
        name="i-heroicons-arrow-path"
        class="animate-spin h-8 w-8 text-gray-400"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useGlobalSalaryPeriod } from '~/composables/useGlobalSalaryPeriod'

interface CityData {
  city: string
  state: string
  median_salary: number
  difference_text: string
  difference_value: number
  is_above: boolean
  url: string
  sample_size?: number
}
interface Props {
  permalink: string
  companyName: string
  role: string
  state: string
  stateCode: string
  preFetchedData?: any
}
const props = defineProps<Props>()
const tableData = ref<CityData[]>([])
const isLoading = ref(true)
const { activePeriod, displayPeriods, adjustValueForPeriod }
  = useGlobalSalaryPeriod()
const periodComparisonLabel = computed(() => {
  const periodName
    = displayPeriods[activePeriod.value].charAt(0).toUpperCase()
      + displayPeriods[activePeriod.value].slice(1)
  return `${periodName} Pay vs. State Avg`
})
const populateTableData = () => {
  isLoading.value = true
  if (props.preFetchedData) {
    try {
      let cities = []
      if (
        props.preFetchedData.cities
        && Array.isArray(props.preFetchedData.cities)
      ) {
        cities = props.preFetchedData.cities
      } else if (
        props.preFetchedData.data
        && props.preFetchedData.data.cities
      ) {
        cities = props.preFetchedData.data.cities
      } else if (
        props.preFetchedData.success
        && props.preFetchedData.data
        && props.preFetchedData.data.cities
      ) {
        cities = props.preFetchedData.data.cities
      } else {
        tableData.value = []
      }
      tableData.value = cities.map((city) => {
        const adjustedSalary = adjustValueForPeriod(city.median_salary)
        const adjustedDifference = adjustValueForPeriod(city.difference_value)
        let differenceText = city.difference_text
        if (differenceText.includes('$')) {
          const matches = differenceText.match(/\$([\d,]+)/)
          if (matches && matches[1]) {
            const formattedAdjustedDifference = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })
              .format(Math.abs(adjustedDifference))
              .replace('$', '')
            differenceText = differenceText.replace(
              matches[1],
              formattedAdjustedDifference
            )
          }
        }
        return {
          ...city,
          median_salary: adjustedSalary,
          difference_value: adjustedDifference,
          difference_text: differenceText
        }
      })
    } catch (error) {
      console.error('Error processing preFetchedData:', error)
      tableData.value = []
    }
  } else {
    tableData.value = []
  }
  isLoading.value = false
}
watch(activePeriod, () => {
  populateTableData()
})
populateTableData()
</script>
